import { Fragment, type ReactNode } from "react";

import { CoachCard } from "~/components/coach-card";
import type { CoachOverview } from "~/lib/coach-grouping";

type ListedCoach = Pick<
  CoachOverview,
  | "id"
  | "username"
  | "fullName"
  | "membershipPlan"
  | "profilePictureUrl"
  | "isFeaturedCoach"
  | "isFoundingCoach"
  | "coachingTitle"
  | "brandLogoUrl"
  | "brandName"
  | "title"
>;

type Props = {
  coaches: ReadonlyArray<ListedCoach>;
  /** Shown in place of the cards when the list is empty. */
  emptyMessage: ReactNode;
  /** Rendered after the 12th card, spanning the grid's columns. */
  midListCta?: ReactNode;
  /** Replaces the grid's default margin, gap and column classes. */
  className?: string;
};

/** The 12th card is followed by the mid-list call to action. */
const MID_LIST_CTA_INDEX = 11;

/**
 * Card grid of a public coach listing (category, subcategory and city pages):
 * one `CoachCard` per coach, an empty-state line, and an optional call to
 * action after the 12th card.
 *
 * Aliases: coach list, coach card grid, listing grid
 */
export function CoachListGrid({
  coaches,
  emptyMessage,
  midListCta,
  className = "mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-3",
}: Props) {
  return (
    <div className={className}>
      {coaches.length === 0 ? (
        <p className="col-span-full text-center text-lg text-gray-500">
          {emptyMessage}
        </p>
      ) : (
        coaches.map((coach, i) => (
          <Fragment key={coach.id}>
            <CoachCard
              coachData={{
                coach: {
                  id: coach.id,
                  username: coach.username,
                  fullName: coach.fullName,
                  membershipPlan: coach.membershipPlan,
                  profilePictureUrl: coach.profilePictureUrl,
                  isFeaturedCoach: coach.isFeaturedCoach,
                  isFoundingCoach: coach.isFoundingCoach,
                },
                profile: {
                  coachingTitle: coach.coachingTitle,
                  brandLogoUrl: coach.brandLogoUrl,
                  brandName: coach.brandName,
                  title: coach.title,
                },
              }}
              source="coach_overview"
            />
            {i === MID_LIST_CTA_INDEX && midListCta}
          </Fragment>
        ))
      )}
    </div>
  );
}
